import {
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';

import {
  AMBIENTES_MAS_DE,
  BoundingBox,
  CARACTERISTICA_LABELS,
  caracteristicaDeTipo,
  FiltrosBusqueda,
  ListaPropiedadesPublica,
  Moneda,
  Operacion,
  OPERACION_LABELS,
  PinMapa,
  RangoPrecios,
  SUPERFICIE_MAX,
  SUPERFICIE_STEP,
  TIPO_PROPIEDAD_LABELS,
  TipoPropiedad,
} from '../../core/models/propiedad.model';
import { Banner, TipoBanner } from '../../core/models/banner.model';
import {
  POR_PAGINA_DESKTOP,
  POR_PAGINA_MOBILE,
  PropiedadesPublicoService,
} from '../../core/services/propiedades-publico.service';
import { BannersPublicoService } from '../../core/services/banners-publico.service';
import { BannerCta } from '../../shared/banner-cta/banner-cta';
import { AutocompleteLocalidad } from '../../shared/autocomplete-localidad/autocomplete-localidad';
import { InputRadio } from '../../shared/input-radio/input-radio';
import { Paginador } from '../../shared/paginador/paginador';
import { PropiedadCard } from '../../shared/propiedad-card/propiedad-card';
import { SliderPrecio } from '../../shared/slider-precio/slider-precio';
import { CamaraMapa, MapaPropiedades } from './mapa-propiedades';
import { Icono } from '../../shared/icono/icono';

/**
 * Piso fijo del slider, igual que `PRECIO_MIN` del backoffice. **No** se usa el
 * `min` que devuelve `/rango-precios`: con el paso de 50.000 contándose desde
 * el piso, un piso de 1.000 deja valores como "9.001.000" en vez de múltiplos
 * redondos. Del endpoint se toma sólo el techo.
 */
const PRECIO_MIN = 0;

/** Paso máximo del slider (`PRECIO_STEP` del backoffice). */
const PRECIO_STEP = 50_000;

/** Paso de repuesto cuando el techo no da para `PRECIO_STEP`. */
export const PRECIO_STEP_CHICO = 100;

/**
 * Paso del slider para un techo dado (FE-109; port de `calcularPrecioStep` del
 * backoffice — conviene mantener las dos copias iguales).
 *
 * `<input type="range">` sanitiza el valor a la grilla `min + n·paso`, así que
 * con el paso fijo de 50.000 un techo más chico deja al control "hasta" sin
 * ninguna posición alcanzable: el único valor válido es el piso, el thumb
 * queda clavado ahí —encimado con el de "desde"— y el máximo no se puede
 * mover. Acá pasa con cualquier búsqueda de alquiler en USD, donde el techo
 * real ronda los miles.
 *
 * Debajo de `PRECIO_STEP` el paso baja a 100 —de a 100, no menos: es la
 * granularidad pedida para que los valores se sigan leyendo como precios—. Un
 * techo menor a 100 igual queda usable porque `alinearPrecioTope` lo sube a la
 * grilla, así que el slider siempre tiene al menos dos posiciones.
 */
export function calcularPrecioStep(max: number): number {
  return max >= PRECIO_STEP ? PRECIO_STEP : PRECIO_STEP_CHICO;
}

/**
 * Techo del slider alineado hacia ARRIBA a la grilla del paso (port de
 * `alinearPrecioTope` del backoffice).
 *
 * Un techo fuera de la grilla es inalcanzable (misma sanitización que arriba):
 * arrastrar "hasta" al extremo derecho daba un valor por debajo del precio más
 * caro publicado y lo escondía de los resultados. Redondear hacia arriba no
 * esconde nada, la banda de precio es un `<=`.
 */
export function alinearPrecioTope(max: number, step: number): number {
  return Math.ceil(max / step) * step;
}

/** Montos del panel de precio: sin decimales, con separador de miles. */
const FORMATO_MONTO = new Intl.NumberFormat('es-AR', {
  maximumFractionDigits: 0,
});

/**
 * Techo de respaldo mientras el backend no respondió, o cuando no hay
 * propiedades en esa moneda y operación (devuelve `null`/`null`).
 */
const PRECIO_MAX_RESPALDO = 100_000_000;

/**
 * Ancho hasta el que el buscador se ve en su layout mobile: el mismo corte
 * que el `@media (max-width: 900px)` del SCSS, donde la barra de filtros se
 * reemplaza por el botón "Filtros" y su hoja desplegable (Figma 157-762).
 * Los dos tienen que moverse juntos.
 */
const ANCHO_MOBILE = 900;

type Vista = 'mapa' | 'grilla';
type PanelFiltro =
  | 'categoria'
  | 'operacion'
  | 'ubicacion'
  | 'precio'
  | 'ambientes';

/**
 * Pantalla de resultados del buscador público (Figma `buscador`, node
 * 244-863) con sus dos vistas: mapa (default de esta pantalla) y grilla.
 *
 * RN02: ambas vistas comparten los mismos filtros — un único estado
 * `filtrosAplicados` alimenta `GET /publico/propiedades` y `GET /publico/
 * propiedades/mapa`.
 *
 * FP-39: elegir un filtro no busca. Los paneles escriben en `borrador` y el
 * botón "Buscar" (`aplicarYBuscar`) es el único que lo copia a
 * `filtrosAplicados`, sincroniza la URL y dispara los requests. Antes sólo el
 * precio y la superficie esperaban al botón; ahora esperan todos.
 *
 * DP02/DP03: la ubicación admite varias zonas (chips); el mapa solo dibuja
 * el contorno cuando hay exactamente una.
 *
 * DP07 (flujo de retorno a fondo): TODO el estado de la pantalla — filtros,
 * vista, cámara del mapa y pin seleccionado — se persiste en los query
 * params. Al volver de la ficha (o compartir la URL) la pantalla se
 * restaura exactamente como estaba.
 *
 * DP08: el buscador del toolbar navega a esta ruta con `?buscar=…`; la
 * suscripción a los query params detecta el cambio externo y relanza la
 * búsqueda (también con la página ya abierta).
 */
@Component({
  selector: 'app-buscador-page',
  imports: [
    FormsModule,
    BannerCta,
    PropiedadCard,
    MapaPropiedades,
    Icono,
    SliderPrecio,
    Paginador,
    AutocompleteLocalidad,
    InputRadio,
  ],
  templateUrl: './buscador-page.html',
  styleUrl: './buscador-page.scss',
})
export class BuscadorPage {
  private readonly service = inject(PropiedadesPublicoService);
  private readonly bannersService = inject(BannersPublicoService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  // ── Estado ──────────────────────────────────────────────────────────────────

  readonly vista = signal<Vista>('mapa');
  /**
   * Borrador de la barra de filtros: lo que el usuario está eligiendo (FP-39).
   * Lo leen las cabeceras de los paneles y la opción marcada como activa, pero
   * no llega a ningún endpoint por sí solo.
   */
  readonly borrador = signal<FiltrosBusqueda>({});
  /**
   * Filtros de la búsqueda vigente: los que viajan a los endpoints y a la URL
   * (DP07). Ningún panel los toca — sólo "Buscar" y la lectura de los params.
   */
  readonly filtrosAplicados = signal<FiltrosBusqueda>({});
  readonly panelAbierto = signal<PanelFiltro | null>(null);
  /** Hoja de filtros mobile (Figma 157-762). */
  readonly filtrosAbiertos = signal(false);

  readonly lista = signal<ListaPropiedadesPublica | null>(null);
  readonly cargandoLista = signal(false);
  readonly pagina = signal(1);

  /**
   * Consulta viva del corte mobile: se mira con `matchMedia` y no con el ancho
   * del arranque para que rotar el teléfono o achicar la ventana también
   * cuenten (ver el listener del constructor).
   */
  private readonly mediaMobile = window.matchMedia(
    `(max-width: ${ANCHO_MOBILE}px)`,
  );
  readonly esMobile = signal(this.mediaMobile.matches);

  /** Tamaño de página del listado: 51 en desktop (#14), 20 en mobile (#20). */
  readonly porPagina = computed(() =>
    this.esMobile() ? POR_PAGINA_MOBILE : POR_PAGINA_DESKTOP,
  );

  /** Falló la carga de resultados (FE-07): se ofrece reintentar. */
  readonly errorBusqueda = signal(false);

  // ── Banners publicitarios (BP-02 / FP-27, FP-19) ───────────────────────────
  // Independientes de los filtros: se piden una sola vez. `null` si la
  // sección no tiene banner activo (u ocurre un error) — sin banner, la
  // sección simplemente no se muestra.
  readonly bannerSuperior = signal<Banner | null>(null);
  readonly bannerInferior = signal<Banner | null>(null);

  readonly pines = signal<PinMapa[]>([]);
  readonly mapaTotal = signal(0);
  readonly mapaTruncado = signal(false);
  readonly cargandoMapa = signal(false);
  /** Contador para forzar el auto-encuadre del mapa al cambiar filtros. */
  readonly encuadre = signal(0);

  // ── Estado restaurado de la URL (DP07) ─────────────────────────────────────

  /** Cámara con la que abre el mapa (vuelta desde la ficha / URL compartida). */
  readonly camaraInicial?: CamaraMapa;
  /** Pin a reseleccionar al abrir el mapa. */
  readonly pinInicial?: string;

  private camaraActual: CamaraMapa | null = null;
  private pinActual: string | null = null;
  /** Clave del estado que esta página escribió en la URL (corta el eco). */
  private claveEstadoUrl = '';
  /** true mientras la primera carga no deba pisar la cámara restaurada. */
  private restaurandoCamara = false;

  // Borradores de los sliders (el resto de los filtros va en `borrador`)
  /**
   * `null` = filtro de precio inactivo ("Todos"). Elegir moneda es lo que lo
   * activa y habilita el slider, igual que en el backoffice: permite pedir
   * "todo en USD" aunque el rango quede completo.
   */
  readonly precioMoneda = signal<Moneda | null>(null);
  /** `null` = el usuario todavía no movió ese extremo: el slider muestra el tope. */
  readonly precioDesde = signal<number | null>(null);
  readonly precioHasta = signal<number | null>(null);

  /**
   * Borrador del slider de superficie. `superficieTocada` es el que decide si
   * la banda viaja: **en reposo no se manda nada**. Mandar `superficieMin=0`
   * por defecto dejaría afuera todas las propiedades sin superficie cargada,
   * que no la tienen porque nadie la cargó, no porque midan 0.
   */
  readonly superficieDesde = signal(0);
  readonly superficieHasta = signal(SUPERFICIE_MAX);
  readonly superficieTocada = signal(false);

  // ── Rango de precios (BP-01 / FP-17) ───────────────────────────────────────

  readonly rangoPrecios = signal<RangoPrecios | null>(null);

  /**
   * Filtros que afectan al rango, serializados. El rango sólo cambia cuando
   * cambia algo que NO es de precio, así que esto evita repedirlo mientras el
   * usuario arrastra el slider (el endpoint es un agregado anónimo, sin
   * throttling ni cache del lado del backend).
   */
  private claveRango = '';

  // ── Constantes para el template ────────────────────────────────────────────

  readonly tiposPropiedad = Object.values(TipoPropiedad);
  readonly operaciones = [Operacion.VENTA, Operacion.ALQUILER];
  readonly opcionesAmbientes = [1, 2, 3, 4, AMBIENTES_MAS_DE];
  readonly ambientesMasDe = AMBIENTES_MAS_DE;
  readonly superficieMax = SUPERFICIE_MAX;
  readonly superficieStep = SUPERFICIE_STEP;
  /** ARS primero: es la moneda por defecto del mercado local. */
  readonly monedas = [Moneda.ARS, Moneda.USD];
  readonly tipoLabels = TIPO_PROPIEDAD_LABELS;
  readonly operacionLabels = OPERACION_LABELS;
  readonly Moneda = Moneda;

  // ── Derivados ──────────────────────────────────────────────────────────────

  /**
   * "Encontrá departamentos" / "Encontrá propiedades" (tagline Figma).
   *
   * Sale de los filtros aplicados, no del borrador: titula los resultados que
   * están en pantalla. Cambiarlo al elegir la categoría diría "Encontrá casas"
   * sobre una grilla que todavía muestra de todo.
   */
  readonly tagline = computed(() => {
    if (this.filtrosAplicados().emprendimiento) return 'Encontrá emprendimientos';
    const tipo = this.filtrosAplicados().tipoPropiedad;
    return `Encontrá ${tipo ? this.tipoLabels[tipo].toLowerCase() : 'propiedades'}`;
  });

  readonly labelCategoria = computed(() => {
    // Caso especial: Emprendimientos es un flag, no un tipo, pero la web
    // pública lo presenta como una categoría más.
    if (this.borrador().emprendimiento) return 'Emprendimientos';
    const tipo = this.borrador().tipoPropiedad;
    return tipo ? this.tipoLabels[tipo] : 'Todas';
  });

  readonly labelOperacion = computed(() => {
    const op = this.borrador().operacion;
    return op ? this.operacionLabels[op] : 'Todas';
  });

  /** Zonas elegidas (DP02/DP03): "Todas", "Palermo" o "Palermo +2". */
  readonly labelUbicacion = computed(() => {
    const zonas = this.borrador().localidades ?? [];
    if (!zonas.length) return 'Todas';
    return zonas.length === 1 ? zonas[0] : `${zonas[0]} +${zonas.length - 1}`;
  });

  readonly zonas = computed(() => this.borrador().localidades ?? []);

  /**
   * Zonas de la búsqueda vigente. El contorno del mapa (FE-04) usa estas y no
   * las del borrador: una zona agregada pero todavía no buscada dibujaría el
   * contorno alrededor de pines que no son de esa zona.
   */
  readonly zonasAplicadas = computed(
    () => this.filtrosAplicados().localidades ?? [],
  );

  /** Término del buscador del toolbar (DP08), visible y removible. */
  readonly terminoBusqueda = computed(
    () => this.filtrosAplicados().buscar ?? null,
  );

  /**
   * Sale del **borrador del panel**, no de los filtros aplicados, para que se
   * actualice mientras se arrastra el slider — igual que `precioLabel()` del
   * backoffice. Como el precio se aplica recién con "Buscar", esta línea es la
   * única devolución en vivo de lo que se está eligiendo.
   */
  readonly labelPrecio = computed(() => {
    const moneda = this.precioMoneda();
    if (!moneda) return 'Todos';
    // Sin precios en esa moneda no se rotula una banda: los números saldrían
    // del techo de respaldo, no de nada publicado.
    if (!this.hayRango()) return `${moneda} sin precios`;
    const monto = (valor: number) => FORMATO_MONTO.format(valor);
    return `${moneda} ${monto(this.sliderDesde())} – ${monto(this.sliderHasta())}`;
  });

  /**
   * Extremos que devuelve el backend según la operación elegida y la moneda
   * del panel, sin alinear a la grilla del slider. Sin operación se toma la
   * unión de venta y alquiler, que es lo que la búsqueda puede devolver.
   *
   * Los `!= null` son deliberados: `min` puede ser **0** con datos reales
   * (todo precio menor a 1000 redondea a 0), y un `??` o un chequeo por
   * veracidad lo descartaría — ver `ExtremosPrecio` en el modelo.
   *
   * Es el insumo del paso y del techo; el slider usa `extremosPrecio`.
   */
  private readonly maxPrecioDisponible = computed<number | null>(() => {
    const rango = this.rangoPrecios();
    if (!rango) return null;

    const moneda = this.precioMoneda();
    // Sin moneda elegida (filtro inactivo) se muestra el techo más alto de las
    // dos, para que la escala no pegue un salto al elegirla.
    const monedas = moneda ? [moneda] : [Moneda.USD, Moneda.ARS];

    // Sin operación se toma la unión de venta y alquiler, que es lo que la
    // búsqueda puede devolver.
    // La del borrador: elegir Alquiler re-escala el slider en el momento, sin
    // esperar a "Buscar". No hace falta repedir nada — la respuesta del
    // endpoint ya trae los dos bloques (venta y alquiler).
    const operacion = this.borrador().operacion;
    const bloques =
      operacion === Operacion.VENTA
        ? [rango.venta]
        : operacion === Operacion.ALQUILER
          ? [rango.alquiler]
          : [rango.venta, rango.alquiler];

    const maxs = bloques
      .flatMap((b) => monedas.map((m) => b[m]?.max))
      .filter((v) => v != null);
    // Sin propiedades en esa moneda el backend manda null/null.
    if (!maxs.length) return null;

    const max = Math.max(...maxs);
    return max > PRECIO_MIN ? max : null;
  });

  /**
   * Extremos crudos para la escala del slider: el techo disponible, o el de
   * respaldo mientras no haya dato. Con la combinación vacía el número no se
   * llega a usar —el slider queda deshabilitado por `hayRango`— pero el paso y
   * la grilla igual necesitan uno válido.
   */
  private readonly extremosPrecioCrudos = computed<{
    min: number;
    max: number;
  }>(() => ({
    min: PRECIO_MIN,
    max: this.maxPrecioDisponible() ?? PRECIO_MAX_RESPALDO,
  }));

  /** Paso del slider: 50.000, o uno más chico si el techo no le da (FE-109). */
  readonly precioStep = computed(() =>
    calcularPrecioStep(this.extremosPrecioCrudos().max),
  );

  /**
   * Extremos del slider: los del backend con el techo subido a la grilla del
   * paso, así el precio más caro publicado siempre es alcanzable.
   */
  readonly extremosPrecio = computed<{ min: number; max: number }>(() => {
    const { min, max } = this.extremosPrecioCrudos();
    return { min, max: alinearPrecioTope(max, this.precioStep()) };
  });

  /**
   * Hay extremos reales del backend para la moneda y operación vigentes.
   *
   * `false` en los dos casos en que el slider no tiene una escala que ofrecer:
   * el rango todavía no llegó, y —lo que antes se colaba— esa combinación no
   * tiene ninguna propiedad publicada (ej. casas en venta en pesos cuando están
   * todas en dólares). Sin esto se caía al techo de respaldo y el panel ofrecía
   * una banda de 0 a 100.000.000 que no venía de ningún precio real.
   */
  readonly hayRango = computed(() => this.maxPrecioDisponible() != null);

  /**
   * Posición efectiva del slider: el borrador acotado a los extremos vigentes.
   *
   * Hace falta porque los extremos se mueven bajo un borrador ya elegido —
   * pasar de Venta a Alquiler baja el techo de 15.000.000 a 2.000— y un
   * "desde" heredado quedaría fuera de escala: el panel llegaba a mostrar
   * "desde 500.000 – hasta 2.000".
   */
  readonly sliderDesde = computed(() =>
    this.acotar(this.precioDesde() ?? this.extremosPrecio().min),
  );

  readonly sliderHasta = computed(() =>
    this.acotar(this.precioHasta() ?? this.extremosPrecio().max),
  );

  private acotar(valor: number): number {
    const { min, max } = this.extremosPrecio();
    return Math.min(Math.max(valor, min), max);
  }

  /**
   * "Buscar" arranca en el estado `Disabled` del Figma (`245:1090` /
   * `244:1456`) y se habilita con la primera interacción con los filtros —
   * incluida la que viene precargada por la URL (FP-18).
   *
   * Es **sticky** a propósito, igual que `hasInteracted` del backoffice: una
   * vez habilitado no vuelve atrás, ni siquiera al limpiar. Derivarlo del
   * estado actual de los filtros lo re-deshabilitaba justo después de
   * "Limpiar", que es cuando el usuario más necesita poder rebuscar.
   */
  readonly huboInteraccion = signal(false);

  // ── Filtro numérico dinámico (FP-38) ───────────────────────────────────────

  /**
   * Qué característica se filtra según la categoría elegida: Casa →
   * dormitorios, Departamento → ambientes, Industria/Terreno/Local/Oficina →
   * superficie. `null` con "Todas", "Especial" o "Emprendimientos", y ahí el
   * filtro directamente no se muestra (D12).
   *
   * Sale del **borrador**, no de los filtros aplicados: si esperara a "Buscar",
   * elegir Departamento no mostraría el filtro de ambientes hasta después de
   * buscar y habría que buscar dos veces para usarlo.
   *
   * Emprendimientos es un flag y no un tipo, así que deja `tipoPropiedad` sin
   * definir y cae en el mismo caso que "Todas".
   */
  readonly caracteristica = computed(() =>
    caracteristicaDeTipo(this.borrador().tipoPropiedad),
  );

  /** Título del filtro: "Dormitorios", "Ambientes" o "Superficie". */
  readonly labelCaracteristica = computed(() => {
    const c = this.caracteristica();
    return c ? CARACTERISTICA_LABELS[c] : '';
  });

  /** Las dos categorías que se filtran por cantidad usan el mismo select. */
  readonly esFiltroPorCantidad = computed(
    () => this.caracteristica() === 'dormitorios' || this.caracteristica() === 'ambientes',
  );

  readonly esFiltroPorSuperficie = computed(
    () => this.caracteristica() === 'superficie',
  );

  readonly labelAmbientes = computed(() => {
    const a = this.borrador().ambientes;
    if (a == null) return 'Todos';
    return a >= AMBIENTES_MAS_DE ? `+${AMBIENTES_MAS_DE}` : `${a}`;
  });

  /**
   * Posición efectiva del slider de superficie: el borrador acotado a
   * `0–SUPERFICIE_MAX`, igual que `sliderDesde`/`sliderHasta` con el precio.
   *
   * Hace falta porque los extremos son fijos pero el borrador puede venir de
   * la URL sin tope (`?supMin=99999`): el control se clavaba en 3000 mientras
   * la cabecera decía "desde 99.999 m²" y se aplicaba ese valor.
   */
  readonly superficieSliderDesde = computed(() =>
    this.acotarSuperficie(this.superficieDesde()),
  );

  readonly superficieSliderHasta = computed(() =>
    this.acotarSuperficie(this.superficieHasta()),
  );

  private acotarSuperficie(valor: number): number {
    return Math.min(Math.max(valor, 0), SUPERFICIE_MAX);
  }

  /**
   * Sale del borrador del panel, para que se actualice mientras se arrastra
   * —igual que el de precio—. "Todas" mientras el usuario no lo haya tocado.
   */
  readonly labelSuperficie = computed(() => {
    if (!this.superficieTocada()) return 'Todas';
    const fmt = (v: number) => FORMATO_MONTO.format(v);
    const hasta = this.superficieSliderHasta();
    return hasta >= SUPERFICIE_MAX
      ? `desde ${fmt(this.superficieSliderDesde())} m²`
      : `${fmt(this.superficieSliderDesde())} – ${fmt(hasta)} m²`;
  });

  /** Todos los filtros se resuelven server-side (RN02). */
  readonly pinesFiltrados = computed(() => this.pines());

  readonly propiedadesGrilla = computed(() => this.lista()?.datos ?? []);

  /** "Encontramos N propiedades para tu búsqueda". */
  readonly totalResultados = computed(() =>
    this.vista() === 'mapa' ? this.mapaTotal() : (this.lista()?.total ?? 0),
  );

  /** "1 propiedad" / "24 propiedades". */
  readonly totalResultadosLabel = computed(() =>
    this.etiquetaPropiedades(this.totalResultados()),
  );

  /** "Mostrando 51 de 120 propiedades" (a la izquierda del paginador). */
  readonly infoPaginacion = computed(() => {
    const lista = this.lista();
    if (!lista) return '';
    const mostradas = this.propiedadesGrilla().length;
    return `Mostrando ${mostradas} de ${this.etiquetaPropiedades(lista.total)}`;
  });

  /** El sustantivo concuerda con el número que lo precede. */
  private etiquetaPropiedades(cantidad: number): string {
    return `${cantidad} ${cantidad === 1 ? 'propiedad' : 'propiedades'}`;
  }

  constructor() {
    this.cargarBanners();

    // #20: el tamaño de página depende del ancho, así que cruzar el corte
    // obliga a repedir el listado. Se vuelve a la página 1 a propósito: con el
    // tamaño nuevo la página vigente puede no existir (yendo de 20 a 51 hay
    // menos páginas) y, aunque exista, ya no muestra lo mismo.
    //
    // Va por `buscar()` y no por `cargarLista()` para no dejar colgado el
    // cartel de error de FE-07 sobre una carga que salió bien: sólo `buscar()`
    // baja `errorBusqueda`. El rango no se repide igual — `cargarRango()` corta
    // por `claveRango` cuando los filtros no cambiaron, que es este caso.
    const alCruzarCorte = (e: MediaQueryListEvent) => {
      if (e.matches === this.esMobile()) return;
      this.esMobile.set(e.matches);
      this.pagina.set(1);
      this.buscar();
    };
    this.mediaMobile.addEventListener('change', alCruzarCorte);
    inject(DestroyRef).onDestroy(() =>
      this.mediaMobile.removeEventListener('change', alCruzarCorte),
    );

    // DP07: restaurar TODO el estado desde la URL (filtros, vista, cámara,
    // pin). También cubre la preselección desde la home (categoria /
    // emprendimiento) porque usa los mismos query params.
    const params = this.route.snapshot.queryParamMap;
    this.aplicarParams(params);
    this.camaraInicial = this.parsearCamara(params.get('cam'));
    this.pinInicial = params.get('pin') ?? undefined;
    this.camaraActual = this.camaraInicial ?? null;
    this.pinActual = params.get('pin');
    this.restaurandoCamara = !!this.camaraInicial;
    // Clave calculada desde los params crudos: la primera emisión de la
    // suscripción (que repite la URL actual) queda descartada con certeza.
    this.claveEstadoUrl = JSON.stringify(this.paramsDeParamMap(params));

    // DP08: cambios externos de query params (p. ej. una búsqueda desde el
    // toolbar con esta página ya abierta) relanzan la búsqueda. Los cambios
    // que escribe esta misma página (clave igual) se ignoran.
    this.route.queryParamMap.subscribe((p) => {
      const clave = JSON.stringify(this.paramsDeParamMap(p));
      if (clave === this.claveEstadoUrl) return;
      this.aplicarParams(p);
      this.claveEstadoUrl = clave;
      this.pagina.set(1);
      this.buscar();
    });

    this.buscar();
  }

  // ── Estado ⇄ URL (DP07 / DP08) ─────────────────────────────────────────────

  /** Aplica los query params al estado interno (filtros, vista, borradores). */
  private aplicarParams(params: ParamMap): void {
    this.vista.set(params.get('vista') === 'grilla' ? 'grilla' : 'mapa');

    const categoria = params.get('categoria');
    const operacion = params.get('operacion');
    const ambientes = Number(params.get('ambientes'));
    const desde = params.get('desde');
    const hasta = params.get('hasta');
    const moneda = params.get('moneda');
    const localidades = params.getAll('localidad');

    const operacionValida = (Object.values(Operacion) as string[]).includes(
      operacion ?? '',
    )
      ? (operacion as Operacion)
      : undefined;

    const filtros: FiltrosBusqueda = {
      tipoPropiedad: this.tiposPropiedad.includes(categoria as TipoPropiedad)
        ? (categoria as TipoPropiedad)
        : undefined,
      emprendimiento: params.get('emprendimiento') ? true : undefined,
      operacion: operacionValida,
      localidades: localidades.length ? localidades : undefined,
      buscar: params.get('buscar') ?? undefined,
      ambientes:
        Number.isInteger(ambientes) &&
        ambientes >= 1 &&
        ambientes <= AMBIENTES_MAS_DE
          ? ambientes
          : undefined,
      superficieMin: this.numeroParam(params.get('supMin')),
      superficieMax: this.numeroParam(params.get('supMax')),
      ...this.bandaDePrecio(
        operacionValida,
        desde ? Number(desde) : undefined,
        hasta ? Number(hasta) : undefined,
        moneda === Moneda.ARS ? Moneda.ARS : moneda ? Moneda.USD : undefined,
      ),
    };
    // La URL siempre trae una búsqueda ya aplicada (link compartido, vuelta de
    // la ficha, tile de la home), así que arranca con borrador y aplicados
    // iguales: no hay nada pendiente de accionar.
    this.borrador.set(filtros);
    this.filtrosAplicados.set(filtros);

    // Prefill de los borradores de los sliders
    this.precioDesde.set(filtros.precioMin ?? filtros.precioAlquilerMin ?? null);
    this.precioHasta.set(filtros.precioMax ?? filtros.precioAlquilerMax ?? null);
    // Sin banda en la URL el filtro arranca inactivo (sin moneda).
    this.precioMoneda.set(filtros.monedaVenta ?? filtros.monedaAlquiler ?? null);

    // Superficie: se considera tocada sólo si la URL trae alguno de los dos
    // extremos, para no mandar la banda en una carga limpia.
    const tieneSuperficie =
      filtros.superficieMin != null || filtros.superficieMax != null;
    this.superficieTocada.set(tieneSuperficie);
    this.superficieDesde.set(filtros.superficieMin ?? 0);
    this.superficieHasta.set(filtros.superficieMax ?? SUPERFICIE_MAX);

    // Llegar con filtros puestos (link compartido, categoría desde la home)
    // cuenta como interacción: "Buscar" tiene que estar disponible (FP-18).
    if (this.hayFiltrosEn(filtros)) this.huboInteraccion.set(true);
  }

  /** true si el estado trae al menos un filtro aplicado. */
  private hayFiltrosEn(f: FiltrosBusqueda): boolean {
    return !!(
      f.tipoPropiedad ||
      f.emprendimiento ||
      f.operacion ||
      f.localidades?.length ||
      f.buscar ||
      f.ambientes != null ||
      f.precioMin != null ||
      f.precioMax != null ||
      f.precioAlquilerMin != null ||
      f.precioAlquilerMax != null ||
      f.superficieMin != null ||
      f.superficieMax != null
    );
  }

  /**
   * Estado actual serializado como query params (sin cámara ni pin). Sale de
   * los filtros aplicados: la URL representa la búsqueda que se está viendo,
   * nunca un borrador a medio elegir.
   */
  private paramsDeEstado(): Params {
    const f = this.filtrosAplicados();
    return {
      vista: this.vista() === 'grilla' ? 'grilla' : null,
      categoria: f.tipoPropiedad ?? null,
      emprendimiento: f.emprendimiento ? '1' : null,
      operacion: f.operacion ?? null,
      localidad: f.localidades?.length ? f.localidades : null,
      buscar: f.buscar ?? null,
      ambientes: f.ambientes != null ? String(f.ambientes) : null,
      desde: this.precioParam(f.precioMin ?? f.precioAlquilerMin),
      hasta: this.precioParam(f.precioMax ?? f.precioAlquilerMax),
      moneda: (f.monedaVenta ?? f.monedaAlquiler) || null,
      supMin: this.precioParam(f.superficieMin),
      supMax: this.precioParam(f.superficieMax),
    };
  }

  /** Misma estructura que `paramsDeEstado` pero desde un ParamMap entrante. */
  private paramsDeParamMap(p: ParamMap): Params {
    const localidades = p.getAll('localidad');
    return {
      vista: p.get('vista') === 'grilla' ? 'grilla' : null,
      categoria: p.get('categoria'),
      emprendimiento: p.get('emprendimiento') ? '1' : null,
      operacion: p.get('operacion'),
      localidad: localidades.length ? localidades : null,
      buscar: p.get('buscar'),
      ambientes: p.get('ambientes'),
      desde: p.get('desde'),
      hasta: p.get('hasta'),
      moneda: p.get('moneda'),
      supMin: p.get('supMin'),
      supMax: p.get('supMax'),
    };
  }

  private precioParam(valor?: number): string | null {
    return valor != null ? String(valor) : null;
  }

  /** Query param numérico; `undefined` si falta o no es un número válido. */
  private numeroParam(valor: string | null): number | undefined {
    if (!valor) return undefined;
    const n = Number(valor);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  }

  /** Escribe el estado completo en la URL sin agregar entradas al historial. */
  private sincronizarUrl(): void {
    const estado = this.paramsDeEstado();
    this.claveEstadoUrl = JSON.stringify(estado);
    const c = this.camaraActual;
    void this.router.navigate([], {
      queryParams: {
        ...estado,
        pin: this.pinActual,
        cam: c ? `${c.lat.toFixed(5)},${c.lng.toFixed(5)},${c.zoom}` : null,
      },
      replaceUrl: true,
    });
  }

  private parsearCamara(cam: string | null): CamaraMapa | undefined {
    if (!cam) return undefined;
    const [lat, lng, zoom] = cam.split(',').map(Number);
    return Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(zoom)
      ? { lat, lng, zoom }
      : undefined;
  }

  /** DP07: el usuario movió el mapa — persistir cámara en la URL. */
  onCamaraChange(camara: CamaraMapa): void {
    this.camaraActual = camara;
    this.sincronizarUrl();
  }

  /** DP07: cambió el pin seleccionado — persistirlo en la URL. */
  onSeleccionChange(pin: string | null): void {
    this.pinActual = pin;
    this.sincronizarUrl();
  }

  // ── Filtros ────────────────────────────────────────────────────────────────

  togglePanel(panel: PanelFiltro): void {
    this.panelAbierto.update((actual) => (actual === panel ? null : panel));
  }

  cerrarPaneles(): void {
    this.panelAbierto.set(null);
  }

  /** Selección de categoría (excluyente con Emprendimientos en la UI). */
  setCategoria(tipo?: TipoPropiedad): void {
    this.actualizarBorrador({
      tipoPropiedad: tipo,
      emprendimiento: undefined,
      ...this.limpiarFiltroNumerico(),
      ...this.limpiarBandaDePrecio(),
    });
    this.cargarRango();
  }

  /**
   * Cambiar de categoría suelta el filtro numérico anterior. Sin esto, pasar
   * de Departamento (ambientes = 3) a Industria arrastraría un `ambientesMin`
   * que las industrias no pueden cumplir —no cargan ambientes ni dormitorios—
   * y la búsqueda devolvería cero sin que nada lo explique. Lo mismo al revés
   * con la banda de superficie.
   */
  private limpiarFiltroNumerico(): Partial<FiltrosBusqueda> {
    this.superficieDesde.set(0);
    this.superficieHasta.set(SUPERFICIE_MAX);
    this.superficieTocada.set(false);
    return {
      ambientes: undefined,
      superficieMin: undefined,
      superficieMax: undefined,
    };
  }

  /**
   * Cambiar de categoría o de operación suelta la banda de precio elegida
   * (FP-42): la escala pasa a ser otra —los extremos se repiden por categoría y
   * se releen por operación— y arrastrar el rango de antes deja al usuario
   * filtrando por una banda que no eligió para esta combinación. Vuelve a
   * "Todos" y se re-expresa contra la escala nueva, igual que el filtro
   * numérico.
   */
  private limpiarBandaDePrecio(): Partial<FiltrosBusqueda> {
    this.precioMoneda.set(null);
    this.precioDesde.set(null);
    this.precioHasta.set(null);
    return {
      precioMin: undefined,
      precioMax: undefined,
      monedaVenta: undefined,
      precioAlquilerMin: undefined,
      precioAlquilerMax: undefined,
      monedaAlquiler: undefined,
    };
  }

  /**
   * Caso especial: "Emprendimientos" se muestra como categoría pero en el
   * backend es el flag `emprendimiento` (ortogonal al tipo, spec §7.1).
   */
  setCategoriaEmprendimientos(): void {
    this.actualizarBorrador({
      tipoPropiedad: undefined,
      emprendimiento: true,
      ...this.limpiarFiltroNumerico(),
      ...this.limpiarBandaDePrecio(),
    });
    this.cargarRango();
  }

  setOperacion(operacion?: Operacion): void {
    // La banda no se re-mapea de venta a alquiler: se suelta (FP-42). Los topes
    // de las dos operaciones no son comparables —USD 15.000.000 contra USD
    // 2.000— así que trasladar el rango elegido daba una banda que el usuario
    // nunca eligió para esta operación.
    this.actualizarBorrador({ operacion, ...this.limpiarBandaDePrecio() });
  }

  setAmbientes(ambientes?: number): void {
    this.actualizarBorrador({ ambientes });
  }

  // ── Superficie (FP-38) ─────────────────────────────────────────────────────

  onSuperficieDesde(valor: number): void {
    this.superficieDesde.set(valor);
    this.superficieTocada.set(true);
    this.huboInteraccion.set(true);
  }

  onSuperficieHasta(valor: number): void {
    this.superficieHasta.set(valor);
    this.superficieTocada.set(true);
    this.huboInteraccion.set(true);
  }

  /** Vuelve la superficie a "Todas": sin banda, como si no se hubiera tocado. */
  limpiarSuperficie(): void {
    this.superficieDesde.set(0);
    this.superficieHasta.set(SUPERFICIE_MAX);
    this.superficieTocada.set(false);
    this.huboInteraccion.set(true);
  }

  /**
   * Banda de superficie a enviar. Devuelve los dos extremos en `undefined`
   * mientras el slider no se haya tocado, y omite `superficieMax` cuando quedó
   * en el tope: ese tope es fijo (3000 m²) y **no** es el máximo real
   * publicado, así que mandarlo escondería una propiedad más grande.
   */
  private bandaDeSuperficie(): Partial<FiltrosBusqueda> {
    if (!this.superficieTocada()) {
      return { superficieMin: undefined, superficieMax: undefined };
    }
    // Los acotados, no los borradores crudos: se aplica lo que el slider está
    // mostrando (ver `superficieSliderDesde`).
    const hasta = this.superficieSliderHasta();
    return {
      superficieMin: this.superficieSliderDesde(),
      superficieMax: hasta >= SUPERFICIE_MAX ? undefined : hasta,
    };
  }

  // ── Multi-zona (DP02/DP03) ─────────────────────────────────────────────────

  /**
   * Agrega una zona como chip (sin cerrar el panel, para seguir sumando).
   * La manda el autocomplete (FP-16): una sugerencia elegida o texto libre.
   */
  agregarZona(zona: string): void {
    const limpia = zona.trim();
    if (!limpia) return;
    const actuales = this.borrador().localidades ?? [];
    if (actuales.some((l) => l.toLowerCase() === limpia.toLowerCase())) return;
    this.actualizarBorrador({ localidades: [...actuales, limpia] }, false);
  }

  quitarLocalidad(zona: string): void {
    const restantes = (this.borrador().localidades ?? []).filter(
      (l) => l !== zona,
    );
    this.actualizarBorrador(
      { localidades: restantes.length ? restantes : undefined },
      false,
    );
  }

  limpiarLocalidades(): void {
    this.actualizarBorrador({ localidades: undefined }, false);
  }

  /**
   * DP08: quitar el término buscado desde el toolbar. **Rebusca en el acto**,
   * a diferencia del resto de los filtros (FP-39): el chip vive con el texto de
   * resultados, no en la barra. Si sólo tocara el borrador, el chip se iría de
   * pantalla mientras los resultados siguen filtrados por ese término.
   */
  quitarBusqueda(): void {
    this.borrador.update((f) => ({ ...f, buscar: undefined }));
    this.filtrosAplicados.update((f) => ({ ...f, buscar: undefined }));
    this.pagina.set(1);
    this.pinActual = null;
    this.sincronizarUrl();
    this.buscar();
  }

  /**
   * Cambio de moneda: se descartan los extremos elegidos. Los de una moneda no
   * significan nada en la otra — un "desde" de 5.000 en USD caería por debajo
   * del piso en ARS.
   */
  setPrecioMoneda(moneda: Moneda): void {
    this.precioMoneda.set(moneda);
    this.precioDesde.set(null);
    this.precioHasta.set(null);
    this.huboInteraccion.set(true);
  }

  onPrecioDesde(valor: number): void {
    this.precioDesde.set(valor);
    this.huboInteraccion.set(true);
  }

  onPrecioHasta(valor: number): void {
    this.precioHasta.set(valor);
    this.huboInteraccion.set(true);
  }

  /**
   * Vuelve el filtro de precio a "Todos". Como en el backoffice, sólo resetea
   * el panel: la búsqueda se relanza con "Buscar". Mantiene `huboInteraccion`
   * para no deshabilitar el botón justo cuando hace falta usarlo.
   */
  limpiarPrecio(): void {
    this.precioMoneda.set(null);
    this.precioDesde.set(null);
    this.precioHasta.set(null);
    this.huboInteraccion.set(true);
  }

  /**
   * Botón "Buscar" (FP-39): el único punto que convierte el borrador en
   * búsqueda. Elegir categoría, operación, zona, cantidad o mover los sliders
   * sólo anota; recién acá los filtros llegan a los endpoints y a la URL.
   *
   * Mismo flujo que `search()` → `emitFilters()` del backoffice, y por eso
   * ningún panel tiene "Aplicar".
   *
   * Los sliders aportan sus valores **acotados**, no los borradores crudos: se
   * aplica lo que el control está mostrando (ver `sliderDesde`). Sin moneda el
   * filtro de precio está inactivo y su banda se limpia.
   */
  aplicarYBuscar(): void {
    const moneda = this.precioMoneda();
    const f = this.borrador();
    const filtros: FiltrosBusqueda = {
      ...f,
      ...(moneda
        ? this.bandaDePrecio(
            f.operacion,
            this.sliderDesde(),
            this.sliderHasta(),
            moneda,
          )
        : this.bandaDePrecio(f.operacion)),
      ...this.bandaDeSuperficie(),
    };

    // El borrador también se queda con las bandas resueltas: si el usuario
    // vuelve a tocar Buscar sin cambiar nada, se busca exactamente lo mismo.
    this.borrador.set(filtros);
    this.filtrosAplicados.set(filtros);
    this.huboInteraccion.set(true);
    this.pagina.set(1);
    // El resultado va a cambiar: el pin seleccionado deja de tener sentido.
    this.pinActual = null;
    // Buscar NO cierra el desplegable, como en el backoffice: `search()` sólo
    // emite los filtros y el panel se cierra clickeando su cabecera.
    this.sincronizarUrl();
    this.buscar();
  }

  /**
   * Mapea desde/hasta a la banda correcta del backend: alquiler si la
   * operación elegida es alquiler, venta en cualquier otro caso.
   */
  private bandaDePrecio(
    operacion?: Operacion,
    desde?: number,
    hasta?: number,
    moneda?: Moneda,
  ): Partial<FiltrosBusqueda> {
    const limpio: Partial<FiltrosBusqueda> = {
      precioMin: undefined,
      precioMax: undefined,
      monedaVenta: undefined,
      precioAlquilerMin: undefined,
      precioAlquilerMax: undefined,
      monedaAlquiler: undefined,
    };
    if (desde == null && hasta == null) return limpio;
    const monedaFinal = moneda ?? Moneda.USD;

    const venta = {
      precioMin: desde,
      precioMax: hasta,
      monedaVenta: monedaFinal,
    };
    const alquiler = {
      precioAlquilerMin: desde,
      precioAlquilerMax: hasta,
      monedaAlquiler: monedaFinal,
    };

    if (operacion === Operacion.VENTA) return { ...limpio, ...venta };
    if (operacion === Operacion.ALQUILER) return { ...limpio, ...alquiler };

    // Sin operación elegida se mandan LAS DOS bandas. El backend las combina
    // con OR (ver `aplicarFiltroBandasDePrecio`), que es justo "algo en este
    // presupuesto, sea para comprar o alquilar". Mandar sólo la de venta
    // —lo que se hacía antes— dejaba afuera todos los alquileres: sus filas
    // tienen `precioVenta` en NULL y nunca entran en la banda de venta.
    return { ...limpio, ...venta, ...alquiler };
  }

  /**
   * Anota una elección en el borrador. FP-39: no busca, no toca la URL y no
   * resetea la paginación — de eso se encarga `aplicarYBuscar`. Lo único que
   * cambia afuera del borrador es `huboInteraccion`, que habilita el botón.
   */
  private actualizarBorrador(
    parcial: Partial<FiltrosBusqueda>,
    cerrar = true,
  ): void {
    this.borrador.update((f) => ({ ...f, ...parcial }));
    this.huboInteraccion.set(true);
    if (cerrar) this.cerrarPaneles();
  }

  // ── Búsqueda (RN02: misma query para grilla y mapa) ───────────────────────

  buscar(): void {
    this.errorBusqueda.set(false);
    this.cargarLista();
    this.cargarMapa();
    this.cargarRango();
  }

  private cargarBanners(): void {
    this.bannersService.activos().subscribe({
      next: (banners) => {
        this.bannerSuperior.set(
          banners.find((b) => b.tipo === TipoBanner.WEB_SUPERIOR) ?? null,
        );
        this.bannerInferior.set(
          banners.find((b) => b.tipo === TipoBanner.WEB_INFERIOR) ?? null,
        );
      },
      // Sin banners no hay nada que romper: las secciones ya contemplan null.
      error: () => {},
    });
  }

  /**
   * Rango de precios para los topes del slider (BP-01). Se pide sólo cuando
   * cambió algún filtro que NO es de precio: aplicar una banda no mueve los
   * extremos, y repedirlo ahí haría que el slider se re-escale bajo el dedo
   * del usuario justo después de usarlo.
   *
   * La banda de superficie **sí** entra en la clave: el endpoint la acepta y
   * acota el rango con ella (`industria` da USD 480.000–600.000, y con
   * `superficieMin=1000` da 480.000–480.000). Dejarla afuera hacía que el
   * slider siguiera ofreciendo un techo donde ya no hay nada.
   *
   * Si falla se deja el rango anterior (o `null` la primera vez, que cae al
   * respaldo): no es un error que valga interrumpir la búsqueda.
   */
  private cargarRango(): void {
    // La categoría sale del BORRADOR y no de los filtros aplicados (FP-42):
    // elegirla reescala el slider en el momento, sin esperar a "Buscar" —
    // igual que la operación, que `extremosPrecioCrudos` también lee del
    // borrador. La diferencia es que la operación no necesita repedir nada (la
    // respuesta trae los dos bloques) y la categoría sí, porque el endpoint
    // acota por ella.
    //
    // Con ella viaja el filtro numérico, que `limpiarFiltroNumerico` resetea
    // en el mismo movimiento: si saliera de los aplicados, el rango de la
    // categoría nueva quedaría acotado por un `superficieMin` que el borrador
    // ya soltó y que el próximo "Buscar" no va a mandar. En el resto de los
    // llamadores no cambia nada: `buscar()` corre con borrador y aplicados ya
    // sincronizados.
    //
    // Lo demás (operación, zonas, búsqueda libre) sigue saliendo de la
    // búsqueda vigente: son los que ya acotan los resultados en pantalla, y el
    // texto libre del borrador puede estar a medio tipear.
    const borrador = this.borrador();
    const f: FiltrosBusqueda = {
      ...this.filtrosAplicados(),
      tipoPropiedad: borrador.tipoPropiedad,
      emprendimiento: borrador.emprendimiento,
      ambientes: borrador.ambientes,
      superficieMin: borrador.superficieMin,
      superficieMax: borrador.superficieMax,
    };
    const clave = JSON.stringify([
      f.tipoPropiedad,
      f.operacion,
      f.localidades,
      f.buscar,
      f.ambientes,
      f.superficieMin,
      f.superficieMax,
      f.emprendimiento,
    ]);
    if (clave === this.claveRango) return;
    this.claveRango = clave;

    this.service.rangoPrecios(f).subscribe({
      next: (rango) => this.rangoPrecios.set(rango),
      error: () => {
        // Se reintenta en el próximo cambio de filtros.
        this.claveRango = '';
      },
    });
  }

  private cargarLista(): void {
    this.cargandoLista.set(true);
    this.service
      .listar(this.filtrosAplicados(), this.pagina(), this.porPagina())
      .subscribe({
        next: (resp) => {
          this.lista.set(resp);
          this.cargandoLista.set(false);
        },
        error: () => {
          this.cargandoLista.set(false);
          this.errorBusqueda.set(true);
        },
      });
  }

  private cargarMapa(): void {
    this.cargandoMapa.set(true);
    // DP07: si abrimos con cámara restaurada, pedir pines de ese viewport
    // aproximado no es posible sin el mapa montado; se pide por filtros y
    // NO se auto-encuadra para respetar la cámara restaurada.
    const restaurando = this.restaurandoCamara;
    this.service.buscarParaMapa(this.filtrosAplicados()).subscribe({
      next: (resp) => {
        this.pines.set(resp.pines);
        this.mapaTotal.set(resp.total);
        this.mapaTruncado.set(resp.truncado);
        this.cargandoMapa.set(false);
        // Nuevo set de resultados → auto-encuadre (FE-02), salvo que haya
        // una cámara restaurada de la URL (DP07)
        if (!restaurando) this.encuadre.update((v) => v + 1);
        this.restaurandoCamara = false;
      },
      error: () => {
        this.cargandoMapa.set(false);
        this.errorBusqueda.set(true);
      },
    });
  }

  /** BE-06: el usuario movió el mapa → repedir pines acotados al viewport. */
  onBoundsChange(bbox: BoundingBox): void {
    this.cargandoMapa.set(true);
    this.service.buscarParaMapa(this.filtrosAplicados(), bbox).subscribe({
      next: (resp) => {
        this.pines.set(resp.pines);
        this.mapaTotal.set(resp.total);
        this.mapaTruncado.set(resp.truncado);
        this.cargandoMapa.set(false);
      },
      error: () => this.cargandoMapa.set(false),
    });
  }

  // ── Alternador de vista (FE-01) ────────────────────────────────────────────

  toggleVista(): void {
    this.vista.set(this.vista() === 'mapa' ? 'grilla' : 'mapa');
    this.sincronizarUrl();
  }

  // ── Paginación de la grilla (spec §7.4; 51 en desktop, 20 en mobile) ───────

  irAPagina(pagina: number): void {
    const totalPaginas = this.lista()?.totalPaginas ?? 1;
    if (pagina < 1 || pagina > totalPaginas) return;
    this.pagina.set(pagina);
    this.cargarLista();
  }
}
